import { Fragment, useState, type ReactNode, type ComponentType } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  X,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  UploadCloud,
  StickyNote,
  ChevronDown,
  Home,
  Building2,
  Zap,
  UtensilsCrossed,
  Car,
  Briefcase,
  GraduationCap,
  Shield,
  Accessibility,
  Clock,
  Send,
  AlertOctagon,
  Flag,
  User as UserIcon,
  Calendar,
  FileText,
  IdCard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ModalShell } from '@/components/ModalShell';
import { CinLateCaptureModal, parsePlaceholderCin } from './CinLateCaptureModal';
import { PromotedBanner } from '@/features/clients/PromotedBanner';
import { uploaderApi, type UploaderScreeningDetail, type FlagErrorReason } from '@/features/uploader/uploader.api';
import { cn } from '@/lib/utils';
import { useToastStore } from '@/stores/toast.store';
import { useT, useLang } from '@/i18n';
import { humanizeAnswer, humanizeDomainName, type HrsnDomain } from '@/features/screening/answerHumanizer';
import { StickyNoteCard } from '@/components/StickyNoteCard';

interface Props {
  screeningId: string;
  onClose: () => void;
  /** When true, we're in read-only mode. Hides action buttons (mark uploaded / flag error). */
  readOnly?: boolean;
  /**
   * Which backend path to use for the detail fetch.
   * - 'uploader' (default when readOnly=false): /uploader/screenings/:id
   * - 'admin': /admin/uploader/screenings/:id  (back-compat: readOnly=true with no source)
   * - 'agent-own': /agents/me/screenings/:id  (agent reads their own work)
   */
  source?: 'uploader' | 'admin' | 'agent-own';
}

export function UploaderScreeningDetailModal({ screeningId, onClose, readOnly, source }: Props): JSX.Element | null {
  const t = useT();
  const lang = useLang();
  const qc = useQueryClient();
  const pushToast = useToastStore((s) => s.push);
  // F5-T4 (mockup «Uploader 02 — Modal de revisión», firma F-1): UNA pregunta
  // binaria «¿pudiste cargarlo?» con dos caminos que no se parecen en nada.
  // Murieron: los dos botones paralelos, la casilla «sin Medicaid» del camino
  // subido (el corazón del bug de plata), el campo de pegar ID, y «Cancelar»
  // (que no decía que liberaba el reclamo).
  const [path, setPath] = useState<'yes' | 'no' | null>(null);
  const [yesOpt, setYesOpt] = useState<'complete' | 'adapted' | 'duplicate'>('complete');
  const [adaptedDomain, setAdaptedDomain] = useState('');
  const [noOpt, setNoOpt] = useState<'no_medicaid' | 'other' | 'data_inconsistent' | 'duplicate' | null>(null);
  const [note, setNote] = useState('');
  // F5-T4/U-b (F-1): el cierre por inelegibilidad — SOLO en la vista admin,
  // solo sobre un flaggeado sin-Medicaid no subido. Razon obligatoria.
  const [ineligReason, setIneligReason] = useState('');
  // v0.6.0 Cluster N — CIN late-capture modal state. Set when uploader clicks
  // the rose 'CIN no capturado — agregar' CTA on a HH-promoted client.
  const [cinCaptureOpen, setCinCaptureOpen] = useState<{ clientId: string; hmId: string } | null>(null);

  // Resolve which endpoint to hit. Back-compat: if readOnly is set without
  // an explicit source, treat it as the admin path (existing callers do that).
  const resolvedSource: 'uploader' | 'admin' | 'agent-own' =
    source ?? (readOnly ? 'admin' : 'uploader');
  const detailQ = useQuery({
    queryKey: ['uploader', 'detail', screeningId, resolvedSource],
    queryFn: () => {
      if (resolvedSource === 'admin') return uploaderApi.getAdminDetail(screeningId);
      if (resolvedSource === 'agent-own') return uploaderApi.getOwnAgentDetail(screeningId);
      return uploaderApi.getDetail(screeningId);
    },
  });

  const markMut = useMutation({
    // F-1: sin findhelpId (el dibujo mató el campo — la declaración es
    // user+timestamp) y sin noMedicaid (retirado; el server lo rechaza).
    mutationFn: (body: { uploadKind: 'complete' | 'adapted' | 'duplicate'; adaptedDomain?: string }) =>
      uploaderApi.markUploaded(screeningId, body),
    onSuccess: () => {
      pushToast({ variant: 'success', title: t('uploader.detail.savedToast'), description: t('uploader.detail.savedToastDesc'), durationMs: 4000 });
      qc.invalidateQueries({ queryKey: ['uploader'] });
      onClose();
    },
    onError: () => pushToast({ variant: 'error', title: t('uploader.detail.errorToast'), durationMs: 5000 }),
  });

  const ineligMut = useMutation({
    mutationFn: () => uploaderApi.closeIneligible(screeningId, { reason: ineligReason.trim() }),
    onSuccess: () => {
      pushToast({ variant: 'success', title: t('uploader.inelig.savedToast'), durationMs: 4000 });
      qc.invalidateQueries({ queryKey: ['uploader'] });
      onClose();
    },
    onError: () => pushToast({ variant: 'error', title: t('uploader.detail.errorToast'), durationMs: 5000 }),
  });

  const flagMut = useMutation({
    mutationFn: (body: { reason: FlagErrorReason; reasonText: string }) =>
      uploaderApi.flagError(screeningId, body),
    onSuccess: () => {
      pushToast({ variant: 'success', title: t('uploader.detail.flagError.savedToast'), description: t('uploader.detail.flagError.savedToastDesc'), durationMs: 4000 });
      qc.invalidateQueries({ queryKey: ['uploader'] });
      onClose();
    },
    onError: () => pushToast({ variant: 'error', title: t('uploader.detail.flagError.errorToast'), durationMs: 5000 }),
  });

  const d = detailQ.data;
  const alreadyUploaded = !!d?.findhelp.uploadedAt;
  const flagged = !!d?.findhelp.flaggedError && !alreadyUploaded;
  const safetyConcern = !!d?.safetyConcernFlag;

  const fullName = d ? `${d.client.firstName ?? ''} ${d.client.lastName ?? ''}`.trim() : '';
  const mrnTail = d?.client.cin ? d.client.cin.slice(-6) : null;

  const canReview = !readOnly && !alreadyUploaded;
  // «Con trabajo a medias, cerrar pide confirmación UNA vez; sin trabajo,
  // cierra sin preguntar» (confirmar lo que no cuesta nada enseña a ignorar
  // las confirmaciones).
  const dirty = canReview && path !== null;
  const guardedClose = (): void => {
    if (dirty && !window.confirm(t('uploader.review.closeConfirm'))) return;
    onClose();
  };
  const noteOk = note.trim().length >= 3;
  const confirmDisabled =
    !path
    || (path === 'yes' && yesOpt === 'adapted' && adaptedDomain.trim().length < 2)
    || (path === 'no' && (!noOpt || !noteOk));
  const disabledWhy = !path
    ? t('uploader.review.whyChoosePath')
    : path === 'no' && !noOpt
      ? t('uploader.review.whyChooseReason')
      : path === 'no' && !noteOk
        ? t('uploader.review.whyNeedMessage')
        : path === 'yes' && yesOpt === 'adapted' && adaptedDomain.trim().length < 2
          ? t('uploader.review.whyNeedDomain')
          : null;
  const submitReview = (): void => {
    if (confirmDisabled) return;
    if (path === 'yes') {
      markMut.mutate({
        uploadKind: yesOpt,
        adaptedDomain: yesOpt === 'adapted' ? adaptedDomain.trim() : undefined,
      });
    } else if (path === 'no' && noOpt) {
      flagMut.mutate({ reason: noOpt, reasonText: note.trim() });
    }
  };

  return (
    <>
    <ModalShell open onClose={guardedClose} ariaLabelledBy="uploader-detail-title">
      <div className="flex flex-col h-full max-h-[85vh]">
        {/* Header — refined hero (F5-R1b: vestido de marca, misma estructura) */}
        <div className="px-6 pt-5 pb-4 border-b border-nav-card shrink-0 bg-gradient-to-b from-nav-soft/70 to-transparent">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              {detailQ.isLoading ? (
                <div className="space-y-2">
                  <div className="h-7 w-56 bg-nav-hair rounded animate-pulse" />
                  <div className="h-4 w-32 bg-nav-hair rounded animate-pulse" />
                </div>
              ) : d ? (
                <>
                  <h2
                    id="uploader-detail-title"
                    className="font-head text-2xl font-extrabold uppercase tracking-tight text-nav-navy truncate leading-tight"
                  >
                    {fullName || t('uploader.detail.title')}
                  </h2>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {mrnTail && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono font-medium bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2]">
                        MRN ····{mrnTail}
                      </span>
                    )}
                    <span className="text-xs text-nav-slate">#{screeningId.slice(0, 8)}</span>
                  </div>
                </>
              ) : (
                <h2 id="uploader-detail-title" className="font-head text-lg font-bold text-nav-navy">
                  {t('uploader.detail.title')}
                </h2>
              )}
            </div>

            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <button
                onClick={guardedClose}
                className="p-1.5 rounded-md hover:bg-nav-hair text-nav-slate transition-colors"
                aria-label={canReview ? t('uploader.review.closeReleaseAria') : t('uploader.action.close')}
                title={canReview ? t('uploader.review.closeReleaseAria') : undefined}
              >
                <X className="h-4 w-4" />
              </button>
              {d && (
                <div className="flex flex-col items-end gap-1">
                  {safetyConcern && (
                    <StatusPill tone="rose" Icon={AlertTriangle}>
                      {t('uploader.detail.safetyFlag')}
                    </StatusPill>
                  )}
                  {alreadyUploaded && !d.findhelp.noMedicaid && (
                    <StatusPill tone="mint" Icon={CheckCircle2}>
                      {t('uploader.detail.findhelpUploaded')}
                    </StatusPill>
                  )}
                  {alreadyUploaded && d.findhelp.noMedicaid && (
                    <StatusPill tone="sky" Icon={CheckCircle2}>
                      {t('uploader.detail.markUploaded.noMedicaidLabel')}
                    </StatusPill>
                  )}
                  {flagged && (
                    <StatusPill tone="rose" Icon={AlertOctagon}>
                      {t('uploader.detail.findhelpFlagged')}
                    </StatusPill>
                  )}
                  {!alreadyUploaded && !flagged && (
                    <StatusPill tone="peach" Icon={Clock}>
                      {t('uploader.detail.findhelpPending')}
                    </StatusPill>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* F5-T4: la barra del reclamo — cerrar libera, dicho acá (1 de 3).
            Sin cuenta regresiva: el payload no trae el vencimiento del claim
            (desviación anotada; candidato U-b). */}
        {canReview && (
          <div className="flex items-center gap-2.5 flex-wrap min-h-[44px] px-6 py-2 bg-nav-tealTint border-b border-nav-teal/40 text-[13px] text-nav-tealDark shrink-0" data-t4="claim-bar">
            <span className="w-2 h-2 rounded-full bg-nav-teal animate-pulse motion-reduce:animate-none flex-none" aria-hidden />
            <span className="flex-1 min-w-[180px]"><b>{t('uploader.review.claimTitle')}</b></span>
            <span className="text-[12px] opacity-80">{t('uploader.review.claimCloseNote')}</span>
          </div>
        )}

        {/* Body — internal scroll (F5-R1b: tarjetas blancas sobre nav-soft) */}
        <div className="flex-1 overflow-y-auto modal-scroll px-6 py-5 bg-nav-soft/60">
          {detailQ.isLoading ? (
            <div className="py-12 text-center"><Loader2 className="h-6 w-6 text-nav-teal animate-spin mx-auto" /></div>
          ) : detailQ.error || !d ? (
            <div className="py-12 text-center text-sm text-nav-red">{t('uploader.queue.couldNotLoad')}</div>
          ) : (
            <>
              <DetailBody d={d} t={t} lang={lang} onCinCaptureClick={setCinCaptureOpen} />
              {/* F-1/U-b: la disposicion final del sin-Medicaid real es del
                  ADMIN. Solo vista admin + flaggeado sin-Medicaid + no subido. */}
              {resolvedSource === 'admin' && d.noMedicaidFlaggedAt && !d.findhelp.uploadedAt && (
                d.ineligibilityClosedAt ? (
                  <div className="mt-5 rounded-[10px] bg-nav-soft border border-nav-card px-4 py-3 text-[13px] text-nav-slate" data-ub="inelig-cerrado">
                    {t('uploader.inelig.closedLine')}
                  </div>
                ) : (
                  <div className="mt-5 rounded-[12px] bg-nav-redTint/60 border-[1.5px] border-nav-red/50 px-4 py-3.5" data-ub="inelig-cierre">
                    <p className="m-0 mb-1 font-head text-[13.5px] font-bold text-nav-red">{t('uploader.inelig.title')}</p>
                    <p className="m-0 mb-2.5 text-[12.5px] leading-relaxed text-nav-red">{t('uploader.inelig.body')}</p>
                    <input
                      type="text"
                      value={ineligReason}
                      onChange={(e) => setIneligReason(e.target.value)}
                      placeholder={t('uploader.inelig.reasonPh')}
                      aria-label={t('uploader.inelig.reasonPh')}
                      className="w-full box-border h-[40px] px-3 mb-2.5 text-sm border-[1.5px] border-ink-200 rounded-lg focus:outline-none focus:border-nav-red"
                    />
                    <Button
                      size="sm"
                      disabled={ineligReason.trim().length < 3 || ineligMut.isPending}
                      onClick={() => ineligMut.mutate()}
                      className="bg-nav-red hover:opacity-90 text-white"
                    >
                      {ineligMut.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                      {t('uploader.inelig.cta')}
                    </Button>
                  </div>
                )
              )}
              {canReview && (
                <ReviewPaths
                  d={d}
                  path={path}
                  setPath={setPath}
                  yesOpt={yesOpt}
                  setYesOpt={setYesOpt}
                  adaptedDomain={adaptedDomain}
                  setAdaptedDomain={setAdaptedDomain}
                  noOpt={noOpt}
                  setNoOpt={setNoOpt}
                  note={note}
                  setNote={setNote}
                />
              )}
            </>
          )}
        </div>

        {/* Footer — F5-T4: el botón dice la consecuencia, nunca «Guardar»;
            «Cerrar y liberar» reemplaza a «Cancelar» (2 de 3). */}
        <div className="px-6 py-3 border-t border-nav-card bg-white shrink-0">
          <div className="flex items-center justify-end gap-2">
            {!canReview ? (
              <Button variant="secondary" size="sm" onClick={onClose}>
                {t('uploader.action.close')}
              </Button>
            ) : (
              <>
                <Button variant="secondary" size="sm" onClick={guardedClose} className="text-nav-slate">
                  {t('uploader.review.closeRelease')}
                </Button>
                <Button
                  size="sm"
                  onClick={submitReview}
                  disabled={confirmDisabled || markMut.isPending || flagMut.isPending}
                  className={
                    path === 'no'
                      ? 'bg-nav-red hover:opacity-90 text-white shadow-sm'
                      : 'bg-nav-navy hover:bg-nav-navyHover text-white shadow-sm'
                  }
                  data-t4="confirm"
                >
                  {(markMut.isPending || flagMut.isPending) && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                  {path === 'no' ? t('uploader.review.confirmNo') : t('uploader.review.confirmYes')}
                </Button>
              </>
            )}
          </div>
          {canReview && disabledWhy && (
            <p className="m-0 mt-1.5 text-right text-[12.5px] text-nav-slate" data-t4="disabled-why">{disabledWhy}</p>
          )}
        </div>
      </div>
    </ModalShell>
    {/* v0.6.0 Cluster N — CIN late-capture modal overlay. Same portal-to-body
        + stacking pattern as FlagErrorModal. On success it invalidates the
        uploader query family so the detail modal refetches and the rose
        CTA flips to the real CIN value. */}
    {cinCaptureOpen && (
      <CinLateCaptureModal
        open={true}
        clientId={cinCaptureOpen.clientId}
        hmId={cinCaptureOpen.hmId}
        clientLabel={`HH_${cinCaptureOpen.hmId}`}
        invalidateQueryKey={['uploader']}
        onClose={() => setCinCaptureOpen(null)}
      />
    )}
    </>
  );
}

/* ---------- Status pill (header) ---------- */
type Tone = 'mint' | 'lavender' | 'peach' | 'rose' | 'sky' | 'stone';
// F5-R1b — mismas claves (mint/lavender/…), valores re-mapeados al brand:
// mint→sage · lavender/sky→teal · peach→amber · rose→red · stone→neutro.
const TONE_CLASSES: Record<Tone, string> = {
  mint: 'bg-nav-sageTint text-nav-sageText border-nav-sage',
  lavender: 'bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]',
  peach: 'bg-nav-amberTint text-nav-amberText border-[#e6cf9a]',
  rose: 'bg-nav-redTint text-nav-red border-nav-red/40',
  sky: 'bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]',
  stone: 'bg-nav-hair text-nav-slate border-nav-card',
};

function StatusPill({ tone, Icon, children }: { tone: Tone; Icon: ComponentType<{ className?: string }>; children: ReactNode }): JSX.Element {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${TONE_CLASSES[tone]}`}>
      <Icon className="h-3 w-3" />
      {children}
    </span>
  );
}

/* ---------- Body ---------- */

function DetailBody({ d, t, lang, onCinCaptureClick }: {
  d: UploaderScreeningDetail;
  t: ReturnType<typeof useT>;
  lang: 'en' | 'es';
  onCinCaptureClick?: (p: { clientId: string; hmId: string }) => void;
}): JSX.Element {
  const [openSections, setOpenSections] = useState({
    patient: false,
    timeline: false,
    positive: true,
    notes: true,
    answers: true,
    findhelp: false,
  });
  const toggle = (k: keyof typeof openSections) =>
    setOpenSections((s) => ({ ...s, [k]: !s[k] }));
  const noted = d.answers.filter((a) => a.notes && a.notes.trim() !== '');

  // v0.6.0 Fase 11.13 — household linkage display. When the client was
  // promoted from a household_member (created_via='household_promotion'),
  // show PromotedBanner at the top of the detail. hideViewLink=true
  // because uploader role has no /screenings/:id route access (would 403).
  const promotedFrom = d.promotedFrom;
  const parentName = promotedFrom
    ? [promotedFrom.parentClientFirstName, promotedFrom.parentClientLastName]
        .filter(Boolean)
        .join(' ') || null
    : null;

  return (
    <div className="space-y-3">
      {promotedFrom && (
        <PromotedBanner
          parentClientName={parentName}
          parentScreeningId={promotedFrom.sourceScreeningId}
          promotedAt={promotedFrom.promotedAt}
          promotedByName={promotedFrom.promotedByName}
          promotedByEmail={promotedFrom.promotedByEmail}
          hideViewLink={true}
        />
      )}
      {/* v0.6.1 Fase 9.2 — Cluster ED Phase 1.2: surface the most-recent
          EDIT_CLIENT event so the uploader doesn't upload stale data to
          FindHelp. info variant (sky blue) — informational, not blocking. */}
      {d.clientLastEdit && (
        <Alert variant="info" role="status">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
          <div className="min-w-0">
            <AlertTitle>{t('uploader.clientEditNotice.title')}</AlertTitle>
            <AlertDescription>
              {t('uploader.clientEditNotice.body', {
                editor: d.clientLastEdit.editorFullName ?? d.clientLastEdit.editorId,
                role: d.clientLastEdit.editorRole,
                date: new Date(d.clientLastEdit.editedAt).toLocaleString(),
              })}
              {d.clientLastEdit.fieldsChanged.length > 0 && (
                <p className="mt-1 text-xs">
                  <span className="font-medium">
                    {t('uploader.clientEditNotice.fieldsChangedLabel')}:
                  </span>{' '}
                  {d.clientLastEdit.fieldsChanged.join(', ')}
                </p>
              )}
              {d.clientLastEdit.reason && (
                <p className="mt-0.5 text-xs italic opacity-90">
                  {t('uploader.clientEditNotice.reasonLabel')}: {d.clientLastEdit.reason}
                </p>
              )}
            </AlertDescription>
          </div>
        </Alert>
      )}
      {/* PATIENT */}
      <CollapsibleSection
        title={t('screening.detail.section.patient')}
        open={openSections.patient}
        onToggle={() => toggle('patient')}
        Icon={UserIcon}
        tone="lavender"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Field label={lang === 'es' ? 'Apellido' : 'Last name'} value={d.client.lastName ?? '—'} />
          {/* v0.6.0 Cluster N — surface CIN late-capture CTA when the
              displayed CIN matches the HH_<hmId> placeholder pattern
              (Cluster H promote-without-CIN design). Clicking opens
              CinLateCaptureModal → POST /uploader/clients/:cid/capture-cin
              (atomic withPhi TX mirrors HM medicaid_id_enc to clients.cin_enc). */}
          {(() => {
            const placeholderHm = parsePlaceholderCin(d.client.cin);
            if (placeholderHm) {
              return (
                <div className="text-sm">
                  <p className="font-head text-[10px] uppercase tracking-wider text-nav-slate font-bold">
                    {t('uploader.detail.cin')}
                  </p>
                  <button
                    type="button"
                    onClick={() => onCinCaptureClick?.({ clientId: d.client.id, hmId: placeholderHm })}
                    className="mt-0.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-nav-red bg-nav-redTint hover:bg-nav-redTint/70 border border-nav-red/40 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-red/50"
                  >
                    <IdCard className="w-3.5 h-3.5" />
                    {t('cinCapture.cta')}
                  </button>
                </div>
              );
            }
            return <Field label={t('uploader.detail.cin')} value={d.client.cin ?? '—'} mono />;
          })()}
          <Field label={t('uploader.detail.dob')} value={d.client.dob ?? '—'} />
          <Field label={t('uploader.detail.phone')} value={d.client.phoneCell || d.client.phoneHome || '—'} />
          <Field label={t('uploader.detail.address')} value={[d.client.addressLine1, d.client.city, d.client.postalCode].filter(Boolean).join(', ') || '—'} />
          <Field label={t('uploader.detail.preferredLang')} value={d.client.preferredLanguage ?? '—'} />
        </div>
      </CollapsibleSection>

      {/* TIMELINE */}
      <CollapsibleSection
        title={t('screening.detail.section.timeline')}
        open={openSections.timeline}
        onToggle={() => toggle('timeline')}
        Icon={Calendar}
        tone="lavender"
      >
        <Timeline d={d} t={t} />
      </CollapsibleSection>

      {/* POSITIVE FINDINGS */}
      <CollapsibleSection
        title={t('uploader.detail.positiveDomains')}
        open={openSections.positive}
        onToggle={() => toggle('positive')}
        Icon={AlertTriangle}
        tone="peach"
        countBadge={d.positiveDomains.length || undefined}
      >
        {d.positiveDomains.length === 0 ? (
          <EmptyState
            Icon={CheckCircle2}
            tone="mint"
            label={t('uploader.detail.noPositive')}
          />
        ) : (
          <ul className="flex flex-wrap gap-2">
            {d.positiveDomains.map((dom) => {
              const meta = DOMAIN_META[dom as HrsnDomain] ?? DOMAIN_META_DEFAULT;
              const Ic = meta.icon;
              return (
                <li key={dom} className="inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full font-head text-xs font-bold bg-nav-amberTint text-nav-amberText border border-[#e6cf9a]">
                  <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full ${meta.tintBg}`}>
                    <Ic className={`h-3 w-3 ${meta.tintText}`} />
                  </span>
                  {humanizeDomainName(dom as HrsnDomain, lang)}
                </li>
              );
            })}
          </ul>
        )}
        {d.safetyConcernFlag && (
          <p className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-head text-xs font-bold bg-nav-redTint text-nav-red border border-nav-red/40">
            <AlertTriangle className="h-3.5 w-3.5" />
            {t('uploader.detail.safetyFlag')} ({d.safetyTotalScore})
          </p>
        )}
      </CollapsibleSection>

      {/* NOTES — the priority */}
      <CollapsibleSection
        title={t('screening.detail.section.notes')}
        open={openSections.notes}
        onToggle={() => toggle('notes')}
        Icon={StickyNote}
        tone="peach"
        countBadge={noted.length || undefined}
      >
        {noted.length === 0 ? (
          <EmptyState
            Icon={StickyNote}
            tone="stone"
            label={t('screening.detail.section.empty.notes')}
          />
        ) : (
          <div className="space-y-3">
            {noted.map((a) => (
              <StickyNoteCard
                key={a.domain}
                domain={a.domain}
                text={a.notes ?? ''}
                capturedBy={d.agentName ?? undefined}
                capturedAt={d.submittedAt ?? d.startedAt ?? undefined}
                size="full"
              />
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* HRSN ANSWERS */}
      <CollapsibleSection
        title={t('screening.detail.section.answers')}
        open={openSections.answers}
        onToggle={() => toggle('answers')}
        Icon={FileText}
        tone="lavender"
        countBadge={d.answers.length || undefined}
      >
        <div className="space-y-2.5">
          {d.answers.map((a) => {
            const domainKey = a.domain as HrsnDomain;
            const humanRows = humanizeAnswer(domainKey, a.answer, lang);
            const humanDomainName = humanizeDomainName(domainKey, lang);
            const meta = DOMAIN_META[domainKey] ?? DOMAIN_META_DEFAULT;
            const Ic = meta.icon;
            return (
              <article
                key={a.domain}
                className={`relative rounded-[12px] bg-white border-[1.5px] border-nav-card p-4 pl-5 transition-shadow hover:shadow-md ${a.positiveFinding ? '' : ''}`}
              >
                {!a.positiveFinding && (
                  <span aria-hidden className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-nav-sage" />
                )}
                {a.positiveFinding && (
                  <span aria-hidden className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-nav-amber" />
                )}
                <div className="flex items-start gap-3">
                  <div className={`shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full ${meta.tintBg} border ${meta.tintBorder}`}>
                    <Ic className={`h-5 w-5 ${meta.tintText}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h4 className="font-head text-[15px] font-bold text-nav-navy leading-tight">{humanDomainName}</h4>
                        {humanRows.length > 0 && (
                          <p className="text-[11px] uppercase tracking-wide text-nav-slate mt-0.5">
                            {humanRows.length === 1 && humanRows[0] ? humanRows[0].label : `${humanRows.length} ${lang === 'es' ? 'campos' : 'fields'}`}
                          </p>
                        )}
                      </div>
                      {a.positiveFinding && (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-head text-[10px] font-bold bg-nav-amberTint text-nav-amberText border border-[#e6cf9a]">
                          <AlertCircle className="h-3 w-3" />
                          {t('screening.detail.findings.positive')}
                        </span>
                      )}
                    </div>

                    <dl className="mt-2.5 grid grid-cols-1 sm:grid-cols-[minmax(140px,auto)_1fr] gap-x-4 gap-y-1.5">
                      {humanRows.map((row, i) => (
                        <Fragment key={i}>
                          <dt className="text-xs text-nav-slate">{row.label}</dt>
                          <dd className={`text-sm ${row.positive ? 'text-nav-amberText font-semibold' : 'text-nav-navy'}`}>
                            {row.value}
                          </dd>
                        </Fragment>
                      ))}
                    </dl>

                    {a.notes && (
                      <div className="mt-3 inline-flex items-start gap-2 px-3 py-2 rounded-lg bg-nav-amberTint/60 border border-[#e6cf9a] max-w-full">
                        <StickyNote className="h-3.5 w-3.5 text-nav-amberText mt-0.5 shrink-0" />
                        <span
                          className="text-sm text-nav-navy italic leading-relaxed"
                          style={{ fontFamily: '"Georgia", "Charter", "Iowan Old Style", serif' }}
                        >
                          {a.notes}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </CollapsibleSection>

      {/* FINDHELP UPLOAD */}
      <CollapsibleSection
        title={t('screening.detail.section.findhelp')}
        open={openSections.findhelp}
        onToggle={() => toggle('findhelp')}
        Icon={UploadCloud}
        tone="mint"
      >
        <FindhelpCard d={d} t={t} />
      </CollapsibleSection>
    </div>
  );
}

/* ---------- Timeline ---------- */
function Timeline({ d, t }: { d: UploaderScreeningDetail; t: ReturnType<typeof useT> }): JSX.Element {
  type Step = { tone: 'lavender' | 'mint' | 'rose' | 'stone' | 'sky'; Icon: ComponentType<{ className?: string }>; label: string; date: string | null };
  const steps: Step[] = [];

  steps.push({
    tone: 'lavender',
    Icon: UserIcon,
    label: t('screening.detail.timeline.started', {
      agent: d.agentName ?? '—',
      date: formatDate(d.startedAt),
    }),
    date: d.startedAt,
  });

  if (d.submittedAt) {
    steps.push({
      tone: 'mint',
      Icon: Send,
      label: t('screening.detail.timeline.submitted', { date: formatDate(d.submittedAt) }),
      date: d.submittedAt,
    });
  }

  if (d.findhelp.uploadedAt) {
    steps.push({
      tone: d.findhelp.noMedicaid ? 'sky' : 'mint',
      Icon: UploadCloud,
      label: t('screening.detail.timeline.uploaded', {
        uploader: d.findhelp.uploadedByName ?? '—',
        date: formatDate(d.findhelp.uploadedAt),
      }),
      date: d.findhelp.uploadedAt,
    });
  }

  if (d.findhelp.flaggedError && !d.findhelp.uploadedAt) {
    steps.push({
      tone: 'rose',
      Icon: Flag,
      label: t('screening.detail.timeline.flagged', { error: d.findhelp.flaggedError }),
      date: d.findhelp.flaggedAt,
    });
  }

  // F5-R1b — claves intactas, valores al brand.
  const dotTone: Record<Step['tone'], string> = {
    lavender: 'bg-nav-teal border-nav-tealTint',
    mint: 'bg-nav-sage border-nav-sageTint',
    rose: 'bg-nav-red border-nav-redTint',
    stone: 'bg-nav-line border-nav-hair',
    sky: 'bg-nav-teal border-nav-tealTint',
  };

  return (
    <ol className="relative pl-2">
      {/* vertical rail */}
      <span aria-hidden className="absolute left-[11px] top-2 bottom-2 w-px bg-nav-card" />
      {steps.map((s, i) => (
        <li key={i} className="relative flex items-start gap-3 py-1.5">
          <span className={`relative z-10 mt-1 inline-block h-3 w-3 rounded-full border-2 shrink-0 ${dotTone[s.tone]}`} />
          <div className="min-w-0 flex-1 pb-1">
            <p className="font-body text-sm text-nav-navy leading-snug">{s.label}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ---------- FindHelp card ---------- */
function FindhelpCard({ d, t }: { d: UploaderScreeningDetail; t: ReturnType<typeof useT> }): JSX.Element {
  if (d.findhelp.uploadedAt) {
    if (d.findhelp.noMedicaid) {
      return (
        <div className="rounded-[12px] bg-nav-tealTint border-[1.5px] border-nav-teal/40 p-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/70 border border-[#c9e2e2]">
              <CheckCircle2 className="h-5 w-5 text-nav-tealDark" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-head text-sm font-bold text-nav-tealDark">{t('screening.detail.findhelp.noMedicaid')}</p>
              <p className="text-xs text-nav-slate mt-0.5">
                {d.findhelp.uploadedByName ?? '—'} · {formatDate(d.findhelp.uploadedAt)}
              </p>
              {d.findhelp.notes && <p className="mt-2 text-sm text-nav-navy">{d.findhelp.notes}</p>}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="rounded-[12px] bg-nav-sageTint border-[1.5px] border-nav-sage p-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/70 border border-nav-sage">
            <CheckCircle2 className="h-5 w-5 text-nav-sageText" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-head text-sm font-bold text-nav-sageText">{t('screening.detail.findhelp.uploaded')}</p>
            <p className="text-xs text-nav-slate mt-0.5">
              {d.findhelp.uploadedByName ?? '—'} · {formatDate(d.findhelp.uploadedAt)}
            </p>
            {d.findhelp.findhelpId && (
              <p className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono bg-white border border-nav-sage text-nav-navy">
                {d.findhelp.findhelpId}
              </p>
            )}
            {d.findhelp.notes && <p className="mt-2 text-sm text-nav-navy">{d.findhelp.notes}</p>}
          </div>
        </div>
      </div>
    );
  }

  if (d.findhelp.flaggedError) {
    return (
      <div className="rounded-[12px] bg-nav-redTint border-[1.5px] border-nav-red/40 p-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/70 border border-nav-red/40">
            <AlertOctagon className="h-5 w-5 text-nav-red" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-head text-sm font-bold text-nav-red">
              {t('screening.detail.findhelp.flagged', { error: d.findhelp.flaggedError })}
            </p>
            {d.findhelp.flaggedAt && (
              <p className="text-xs text-nav-slate mt-0.5">{formatDate(d.findhelp.flaggedAt)}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[12px] bg-nav-amberTint border-[1.5px] border-[#e6cf9a] p-4">
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/70 border border-[#e6cf9a]">
          <Clock className="h-5 w-5 text-nav-amberText" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-head text-sm font-bold text-nav-amberText">{t('screening.detail.findhelp.pending')}</p>
        </div>
      </div>
    </div>
  );
}

/* ---------- Empty state ---------- */
function EmptyState({ Icon, tone, label }: { Icon: ComponentType<{ className?: string }>; tone: Tone; label: string }): JSX.Element {
  const tints: Record<Tone, string> = {
    mint: 'bg-nav-sageTint border-nav-sage text-nav-sageText',
    lavender: 'bg-nav-tealTint border-[#c9e2e2] text-nav-tealDark',
    peach: 'bg-nav-amberTint border-[#e6cf9a] text-nav-amberText',
    rose: 'bg-nav-redTint border-nav-red/40 text-nav-red',
    sky: 'bg-nav-tealTint border-[#c9e2e2] text-nav-tealDark',
    stone: 'bg-nav-softer border-nav-card text-nav-slate',
  };
  return (
    <div className={`rounded-xl border border-dashed px-4 py-6 text-center ${tints[tone]}`}>
      <Icon className="h-7 w-7 mx-auto mb-2 opacity-50" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

/* ---------- Collapsible section ---------- */
function CollapsibleSection({
  title,
  open,
  onToggle,
  Icon,
  tone = 'lavender',
  countBadge,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  Icon?: ComponentType<{ className?: string }>;
  tone?: Tone;
  countBadge?: number;
  children: ReactNode;
}): JSX.Element {
  const tintBg: Record<Tone, string> = {
    mint: 'bg-nav-sageTint text-nav-sageText',
    lavender: 'bg-nav-tealTint text-nav-tealDark',
    peach: 'bg-nav-amberTint text-nav-amberText',
    rose: 'bg-nav-redTint text-nav-red',
    sky: 'bg-nav-tealTint text-nav-tealDark',
    stone: 'bg-nav-hair text-nav-slate',
  };
  return (
    <section className="rounded-[12px] border-[1.5px] border-nav-card bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2 px-4 py-3 text-left transition-colors ${open ? 'bg-white' : 'hover:bg-nav-softer'} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal/40`}
      >
        <span className="inline-flex items-center gap-2.5 font-head text-sm font-bold text-nav-navy">
          {Icon && (
            <span className={`inline-flex items-center justify-center h-7 w-7 rounded-lg ${tintBg[tone]}`}>
              <Icon className="h-4 w-4" />
            </span>
          )}
          <span className="uppercase tracking-[.12em] text-[11px]">{title}</span>
          {countBadge !== undefined && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2] normal-case tracking-normal">
              {countBadge}
            </span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 text-nav-slate transition-transform duration-200 ${open ? 'rotate-0' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-3 border-t border-nav-hair bg-nav-softer/70 animate-fade-in">{children}</div>}
    </section>
  );
}

/* ---------- Field ---------- */
function Field({ label, value, inline, mono }: { label: string; value: string; inline?: boolean; mono?: boolean }): JSX.Element {
  if (inline) {
    return (
      <p className="text-sm">
        <span className="text-nav-slate">{label}:</span>{' '}
        <span className={`text-nav-navy ${mono ? 'font-mono' : ''}`}>{value}</span>
      </p>
    );
  }
  return (
    <div>
      <p className="font-head text-[11px] uppercase tracking-wider text-nav-slate font-bold">{label}</p>
      <p className={`text-base text-nav-navy break-words mt-0.5 ${mono ? 'font-mono text-sm' : ''}`}>{value}</p>
    </div>
  );
}

/* ---------- Domain meta (icon + pastel tint) ---------- */
type DomainMeta = {
  icon: ComponentType<{ className?: string }>;
  tintBg: string;
  tintText: string;
  tintBorder: string;
};
// F5-R1b — tintes de dominio al brand: teal (vivienda/física), amber
// (servicios), sage (comida/empleo), red SOLO seguridad.
const DOMAIN_META_DEFAULT: DomainMeta = {
  icon: FileText,
  tintBg: 'bg-nav-tealTint',
  tintText: 'text-nav-tealDark',
  tintBorder: 'border-[#c9e2e2]',
};
const DOMAIN_META: Record<HrsnDomain, DomainMeta> = {
  living_situation: { icon: Home, tintBg: 'bg-nav-tealTint', tintText: 'text-nav-tealDark', tintBorder: 'border-[#c9e2e2]' },
  housing_conditions: { icon: Building2, tintBg: 'bg-nav-tealTint', tintText: 'text-nav-tealDark', tintBorder: 'border-[#c9e2e2]' },
  utilities: { icon: Zap, tintBg: 'bg-nav-amberTint', tintText: 'text-nav-amberText', tintBorder: 'border-[#e6cf9a]' },
  food: { icon: UtensilsCrossed, tintBg: 'bg-nav-sageTint', tintText: 'text-nav-sageText', tintBorder: 'border-nav-sage' },
  transportation: { icon: Car, tintBg: 'bg-nav-tealTint', tintText: 'text-nav-tealDark', tintBorder: 'border-[#c9e2e2]' },
  career: { icon: Briefcase, tintBg: 'bg-nav-sageTint', tintText: 'text-nav-sageText', tintBorder: 'border-nav-sage' },
  school: { icon: GraduationCap, tintBg: 'bg-nav-tealTint', tintText: 'text-nav-tealDark', tintBorder: 'border-[#c9e2e2]' },
  safety: { icon: Shield, tintBg: 'bg-nav-redTint', tintText: 'text-nav-red', tintBorder: 'border-nav-red/40' },
  physical_ability: { icon: Accessibility, tintBg: 'bg-nav-tealTint', tintText: 'text-nav-tealDark', tintBorder: 'border-[#c9e2e2]' },
};

/* ---------- Forms (unchanged logic; light touch-ups) ---------- */

/**
 * F5-T4 · «¿Pudiste cargarlo?» — los dos caminos de la regla de plata más
 * peligrosa del sistema, separados hasta el fondo (mockup Uploader 02):
 * dos tarjetas de igual peso con la CONSECUENCIA escrita antes de elegir,
 * sub-panel solo del camino elegido, y «sin Medicaid» UNA sola vez, en el
 * camino de error. Sin campo de pegar ID: la declaración es user+timestamp.
 */
function ReviewPaths({ d, path, setPath, yesOpt, setYesOpt, adaptedDomain, setAdaptedDomain, noOpt, setNoOpt, note, setNote }: {
  d: UploaderScreeningDetail;
  path: 'yes' | 'no' | null;
  setPath: (p: 'yes' | 'no') => void;
  yesOpt: 'complete' | 'adapted' | 'duplicate';
  setYesOpt: (o: 'complete' | 'adapted' | 'duplicate') => void;
  adaptedDomain: string;
  setAdaptedDomain: (v: string) => void;
  noOpt: 'no_medicaid' | 'other' | 'data_inconsistent' | 'duplicate' | null;
  setNoOpt: (o: 'no_medicaid' | 'other' | 'data_inconsistent' | 'duplicate') => void;
  note: string;
  setNote: (v: string) => void;
}): JSX.Element {
  const t = useT();
  const agent = d.agentName ?? t('uploader.review.theAgent');
  const fhUrl = d.findhelpBaseUrl ?? null;

  const yesOpts: Array<{ k: 'complete' | 'adapted' | 'duplicate'; title: string; desc: string }> = [
    { k: 'complete', title: t('uploader.review.yes.complete'), desc: t('uploader.review.yes.completeDesc') },
    { k: 'adapted', title: t('uploader.review.yes.adapted'), desc: t('uploader.review.yes.adaptedDesc') },
    { k: 'duplicate', title: t('uploader.review.yes.dup'), desc: t('uploader.review.yes.dupDesc') },
  ];
  // Mapeo al enum vivo del server (mig 028; sin migración — acta E-24):
  // id→no_medicaid (el camino de la plata que U-b vigila) · datos→other
  // (la nota obligatoria lleva el detalle) · respuestas→data_inconsistent ·
  // duplicado→duplicate. cin_invalid se retira de la UI.
  const noOpts: Array<{ k: 'no_medicaid' | 'other' | 'data_inconsistent' | 'duplicate'; title: string; desc: string }> = [
    { k: 'no_medicaid', title: t('uploader.review.no.id'), desc: t('uploader.review.no.idDesc') },
    { k: 'other', title: t('uploader.review.no.datos'), desc: t('uploader.review.no.datosDesc') },
    { k: 'data_inconsistent', title: t('uploader.review.no.respuestas'), desc: t('uploader.review.no.respuestasDesc') },
    { k: 'duplicate', title: t('uploader.review.no.duplicado'), desc: t('uploader.review.no.duplicadoDesc') },
  ];

  const radio = (
    on: boolean,
    danger: boolean,
    onPick: () => void,
    title: string,
    desc: string,
  ): JSX.Element => (
    <button
      key={title}
      type="button"
      aria-pressed={on}
      onClick={onPick}
      className={cn(
        'flex items-start gap-2.5 w-full box-border min-h-[44px] px-3.5 py-3 rounded-[10px] border-[1.5px] bg-white text-left transition-colors',
        on ? (danger ? 'border-nav-red' : 'border-nav-teal') : 'border-ink-200 hover:border-nav-teal',
      )}
    >
      <span className={cn('flex-none inline-flex items-center justify-center w-[18px] h-[18px] mt-0.5 rounded-full border-[1.5px] bg-white', on ? (danger ? 'border-nav-red' : 'border-nav-teal') : 'border-ink-300')}>
        <span className={cn('w-[9px] h-[9px] rounded-full', on ? (danger ? 'bg-nav-red' : 'bg-nav-teal') : 'bg-transparent')} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-head text-[13.5px] font-bold text-ink-900">{title}</span>
        <span className="block mt-0.5 text-[12.5px] leading-normal text-ink-500">{desc}</span>
      </span>
    </button>
  );

  const copyAll = (): void => {
    const lines = (d.answers ?? []).map((a) => {
      const ans = a.notes ?? JSON.stringify(a.answer);
      return `${a.domain}: ${ans}${a.positiveFinding ? ' (+)' : ''}`;
    });
    void navigator.clipboard?.writeText(lines.join('\n'));
  };

  return (
    <div className="mt-5" data-t4="review-paths">
      {/* Paso 1 · FindHelp en otra pestaña; sin URL, inerte con nota. */}
      <div className="flex items-center gap-3 flex-wrap bg-white border-[1.5px] border-ink-200 rounded-[12px] px-4 py-3 mb-4" data-t4="paso1">
        <span className="flex-1 min-w-[200px] text-[13.5px] leading-normal text-ink-800">
          <b>{t('uploader.review.step1')}</b> {t('uploader.review.step1Desc')}
        </span>
        <Button variant="secondary" size="sm" onClick={copyAll}>
          {t('uploader.review.copyAll')}
        </Button>
        {fhUrl ? (
          <a
            href={fhUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 min-h-[38px] px-3.5 border-[1.5px] border-nav-teal rounded-lg text-[13px] font-head font-bold text-nav-tealDark hover:bg-nav-tealTint no-underline"
          >
            {t('uploader.review.openFindhelp')}
          </a>
        ) : (
          <span className="text-[12px] text-ink-400" data-t4="fh-inert">{t('uploader.review.fhNotConfigured')}</span>
        )}
      </div>

      <p className="m-0 mb-3 font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-tealDark">
        {t('uploader.review.step2')}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <button
          type="button"
          aria-pressed={path === 'yes'}
          onClick={() => setPath('yes')}
          className={cn(
            'block w-full box-border text-left rounded-[12px] border-[1.5px] px-5 py-4 transition-all',
            path === 'yes' ? 'bg-nav-sageTint border-nav-sage' : 'bg-white border-ink-200 hover:border-nav-sage hover:-translate-y-0.5 hover:shadow-md',
          )}
          data-t4="path-yes"
        >
          <span className="flex items-center gap-2.5 mb-2">
            <CheckCircle2 className="h-[18px] w-[18px] text-nav-sageText flex-none" aria-hidden />
            <span className="font-head text-[15px] font-bold text-ink-900">{t('uploader.review.yesTitle')}</span>
          </span>
          <span className="block text-[13px] leading-normal text-nav-sageText">{t('uploader.review.yesConsequence', { agent })}</span>
        </button>

        <button
          type="button"
          aria-pressed={path === 'no'}
          onClick={() => setPath('no')}
          className={cn(
            'block w-full box-border text-left rounded-[12px] border-[1.5px] px-5 py-4 transition-all',
            path === 'no' ? 'bg-nav-redTint border-nav-red/60' : 'bg-white border-ink-200 hover:border-nav-red/60 hover:-translate-y-0.5 hover:shadow-md',
          )}
          data-t4="path-no"
        >
          <span className="flex items-center gap-2.5 mb-2">
            <AlertOctagon className="h-[18px] w-[18px] text-nav-red flex-none" aria-hidden />
            <span className="font-head text-[15px] font-bold text-ink-900">{t('uploader.review.noTitle')}</span>
          </span>
          <span className="block text-[13px] leading-normal text-nav-red">{t('uploader.review.noConsequence', { agent })}</span>
        </button>
      </div>

      {path === 'yes' && (
        <div className="mt-3.5 bg-nav-sageTint/50 border-[1.5px] border-nav-sage rounded-[12px] p-4" data-t4="panel-yes">
          <p className="m-0 mb-3 font-head text-[13.5px] font-bold text-nav-sageText">{t('uploader.review.yesHow')}</p>
          <div className="flex flex-col gap-2">
            {yesOpts.map((o) => radio(yesOpt === o.k, false, () => setYesOpt(o.k), o.title, o.desc))}
          </div>
          {yesOpt === 'adapted' && (
            <input
              type="text"
              value={adaptedDomain}
              onChange={(e) => setAdaptedDomain(e.target.value)}
              placeholder={t('uploader.review.adaptedPlaceholder')}
              aria-label={t('uploader.review.adaptedPlaceholder')}
              className="mt-3 w-full box-border h-[44px] px-3 text-sm border-[1.5px] border-ink-200 rounded-lg focus:outline-none focus:border-nav-teal"
            />
          )}
          <div className="flex items-start gap-2 mt-3.5 text-[12.5px] leading-normal text-nav-sageText">
            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-none" aria-hidden />
            <span>{t(`uploader.review.yesFinal.${yesOpt}` as Parameters<typeof t>[0], { agent })}</span>
          </div>
        </div>
      )}

      {path === 'no' && (
        <div className="mt-3.5 bg-nav-redTint/50 border-[1.5px] border-nav-red/50 rounded-[12px] p-4" data-t4="panel-no">
          <p className="m-0 mb-3 font-head text-[13.5px] font-bold text-nav-red">{t('uploader.review.noWhat')}</p>
          <div className="flex flex-col gap-2">
            {noOpts.map((o) => radio(noOpt === o.k, true, () => setNoOpt(o.k), o.title, o.desc))}
          </div>
          <label className="block mt-3.5">
            <span className="block mb-1 text-[12px] font-head font-bold text-ink-700">
              {t('uploader.review.noteLabel')} <span className="text-nav-red">· {t('uploader.review.required')}</span>
            </span>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('uploader.review.notePlaceholder')}
              className="w-full box-border px-3 py-2 text-sm border-[1.5px] border-ink-200 rounded-lg focus:outline-none focus:border-nav-red"
            />
          </label>
          <div className="flex items-start gap-2 mt-2 text-[12.5px] leading-normal text-nav-red">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" aria-hidden />
            <span>{t('uploader.review.noFinal', { agent })}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}

